import { useState } from 'react';

import { OverlayDialog } from '../../components/overlay-dialog';
import { Segmented } from '../../components/segmented-control';
import { TextField } from '../../components/text-field';
import type { StatOption, StatOptionInput } from '../../lib/api-types';

import { DEFAULT_PERSONNEL_COPY, personnelOptionText } from './default-personnel';
import {
  CONCEPT_DESIGN_LABELS,
  CONCEPT_DESIGN_TYPES,
  readDesignType,
  toggleDesignType,
  type ConceptDesignType,
} from './design-type';

export interface OptionEditorProps {
  title: string;
  /** `null` = add. */
  initial: StatOption | null;
  onCancel: () => void;
  onSubmit: (input: StatOptionInput) => Promise<void>;
  /** Offensive concepts: show the Run / Pass / RPO design toggle and send `design_type`. */
  showDesign?: boolean;
  /**
   * Formations: the same side's PERSONNEL options for the "Default personnel"
   * picker (sends `personnel_group_id`). `undefined` = no picker (every other
   * stat); `null` = still loading; `'error'` = the list could not be loaded.
   */
  personnelOptions?: readonly StatOption[] | null | 'error';
}

const LABEL_MAX = 80;
const NOTES_MAX = 500;

/** Add / edit sheet for one option. The stored code is derived from the label on add. */
export function OptionEditor({
  title,
  initial,
  onCancel,
  onSubmit,
  showDesign = false,
  personnelOptions,
}: OptionEditorProps): JSX.Element {
  const showPersonnel = personnelOptions !== undefined;
  const [personnel, setPersonnel] = useState<string>(initial?.personnel_group_id ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [isEnabled, setIsEnabled] = useState(initial?.is_enabled ?? true);
  const [design, setDesign] = useState<ConceptDesignType | null>(
    readDesignType(initial?.design_type)
  );
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const submit = async (): Promise<void> => {
    const trimmed = label.trim();
    if (trimmed === '') {
      setError('Give the option a name.');
      return;
    }
    if (trimmed.length > LABEL_MAX) {
      setError(`Names are at most ${LABEL_MAX} characters.`);
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      await onSubmit({
        label: trimmed,
        notes: notes.trim() === '' ? null : notes.trim(),
        ...(initial === null ? { is_enabled: isEnabled } : {}),
        ...(showDesign ? { design_type: design } : {}),
        ...(showPersonnel && Array.isArray(personnelOptions)
          ? { personnel_group_id: personnel === '' ? null : personnel }
          : {}),
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The option could not be saved.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <OverlayDialog title={title} onClose={onCancel}>
      <form
        className="stack playbook-editor"
        noValidate
        data-testid="playbook-editor"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error !== null ? (
          <p className="banner banner--error" role="alert">
            {error}
          </p>
        ) : null}
        <TextField
          label="Name"
          name="label"
          value={label}
          maxLength={LABEL_MAX}
          autoFocus
          placeholder="What the coach calls it"
          onChange={(event) => setLabel(event.target.value)}
        />
        {initial !== null ? (
          <p className="muted playbook-editor__code">
            Stored as <code>{initial.value}</code> on plays — the code never changes so history
            stays intact.
          </p>
        ) : null}
        {showDesign ? (
          <div className="field playbook-editor__design" data-testid="playbook-design">
            <span className="field__label" id="playbook-design-label">
              Design
            </span>
            <Segmented<ConceptDesignType | ''>
              label="Design"
              semantics="group"
              className="playbook-editor__design-toggle"
              testId="playbook-design-toggle"
              options={CONCEPT_DESIGN_TYPES.map((type) => ({
                value: type,
                label: CONCEPT_DESIGN_LABELS[type],
              }))}
              value={design ?? ''}
              onChange={(next) => {
                if (next !== '') setDesign((current) => toggleDesignType(current, next));
              }}
            />
            <span className="muted playbook-editor__design-hint" data-testid="playbook-design-hint">
              {design === null
                ? 'Not set — tap Run, Pass or RPO.'
                : `${CONCEPT_DESIGN_LABELS[design]} — tap it again to clear.`}{' '}
              Picking this concept on Play Entry fills in the play&apos;s Designed Type.
            </span>
          </div>
        ) : null}
        {showPersonnel ? (
          <PersonnelPicker
            options={personnelOptions ?? null}
            value={personnel}
            onChange={setPersonnel}
          />
        ) : null}
        <label className="field">
          <span className="field__label">Notes (staff only)</span>
          <textarea
            className="field__control"
            name="notes"
            rows={3}
            maxLength={NOTES_MAX}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
        {initial === null ? (
          <label className="field field--inline">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
            <span>Offer on Play Entry right away</span>
          </label>
        ) : null}
        <div className="playbook-editor__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={isBusy}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={isBusy}
            data-testid="playbook-editor-save"
          >
            {isBusy ? 'Saving…' : initial === null ? 'Add option' : 'Save'}
          </button>
        </div>
      </form>
    </OverlayDialog>
  );
}

/**
 * "Default personnel" dropdown on a formation: None, or one of the same
 * side's personnel groups. A tagged group that has since been disabled stays
 * listed (marked) so saving the formation never silently drops it.
 */
function PersonnelPicker({
  options,
  value,
  onChange,
}: {
  options: readonly StatOption[] | null | 'error';
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const list = Array.isArray(options) ? options : [];
  const visible = list.filter((option) => option.is_enabled || option.option_id === value);
  const status =
    options === null
      ? DEFAULT_PERSONNEL_COPY.loading
      : options === 'error'
        ? DEFAULT_PERSONNEL_COPY.unavailable
        : visible.length === 0
          ? DEFAULT_PERSONNEL_COPY.empty
          : DEFAULT_PERSONNEL_COPY.hint;
  return (
    <div className="field playbook-editor__personnel" data-testid="playbook-personnel">
      <label className="field__label" htmlFor="playbook-personnel-select">
        {DEFAULT_PERSONNEL_COPY.label}
      </label>
      <select
        id="playbook-personnel-select"
        className="field__control"
        name="personnel_group_id"
        data-testid="playbook-personnel-select"
        value={value}
        disabled={!Array.isArray(options)}
        aria-describedby="playbook-personnel-hint"
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{DEFAULT_PERSONNEL_COPY.none}</option>
        {visible.map((option) => (
          <option key={option.option_id} value={option.option_id}>
            {personnelOptionText(option)}
            {option.is_enabled ? '' : ` ${DEFAULT_PERSONNEL_COPY.disabled}`}
          </option>
        ))}
      </select>
      <span
        id="playbook-personnel-hint"
        className="muted playbook-editor__design-hint"
        data-testid="playbook-personnel-hint"
      >
        {status}
      </span>
    </div>
  );
}
